import React, { useState, useEffect } from "react";
import {
  Input,
  Form,
  Col,
  Row,
  Button,
  Select,
  Space,
  InputNumber,
  TimePicker,
  DatePicker,
  Radio,
  Checkbox,
  Divider,
  Modal,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import RequestHandler from "../../util/RequestHandler";

const { Option } = Select;

const frequencyPeriods = [
  { value: 1, label: "Günde bir" },
  { value: 2, label: "Haftada bir" },
  { value: 3, label: "Ayda bir" },
];

// EnumSweepRuleEndType: Never = 0, AfterOccurrence = 1, OnDate = 2
const END_TYPE = { NEVER: 0, AFTER_OCCURRENCE: 1, ON_DATE: 2 };

// Şimdilik gönderilecek ödemelerde yalnızca TRY destekleniyor.
const TRY_CURRENCY_ID = 1;

const SweepRuleAddForm = (props) => {
  const { editId, onSuccess } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [receiverAccounts, setReceiverAccounts] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [loadedBanks, setLoadedBanks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [tosBankInfos, setTosBankInfos] = useState([]);
  const [accountBalances, setAccountBalances] = useState({});
  // details içindeki satır banka değişiminde bağımlı select'leri yeniden render etmek için
  const [tick, setTick] = useState(0);

  useEffect(() => {
    getBanks();
    getReceiverAccounts();
    getCurrencies();
    getTosBankInfos();
  }, []);

  useEffect(() => {
    if (editId) {
      RequestHandler.get(`/SweepRule/GetById/${editId}`).then((response) => {
        if (!response) return;
        form.setFieldsValue({
          name: response.name,
          receiverAccountId: response.receiverAccountId,
          executionTime: response.executionTime
            ? moment(response.executionTime, "HH:mm:ss")
            : null,
          frequencyInterval: response.frequencyInterval,
          frequencyPeriod: response.frequencyPeriod,
          endType: response.endType,
          endAfterOccurrences: response.endAfterOccurrences,
          endDate: response.endDate ? moment(response.endDate) : null,
          details: (response.details || []).map((d) => ({
            id: d.id,
            bankId: d.bankId,
            senderAccountId: d.senderAccountId,
            description: d.description,
            amountToSend: d.amountToSend,
            minRemainingBalance: d.minRemainingBalance,
            sendAllBalance: d.sendAllBalance,
            currencyId: d.currencyId,
          })),
        });

        // Düzenlemede detaylardaki bankaların hesaplarını yükle (seçili hesap + eşleşme görünsün)
        const bankIds = [
          ...new Set(
            (response.details || []).map((d) => d.bankId).filter(Boolean)
          ),
        ];
        bankIds.forEach((id) => loadAccountsForBank(id));

        setTick((t) => t + 1);
      });
    }
  }, [editId]);

  useEffect(() => {
    if (!editId) return;
    const details = form.getFieldValue("details") || [];
    details.forEach((d) => {
      if (d?.senderAccountId && isAccountMatched(d.senderAccountId)) {
        fetchAccountBalance(d.senderAccountId);
      }
    });
  }, [paymentAccounts, editId]);

  const getBanks = () => {
    RequestHandler.get(
      `/Bank?$filter=isTosSupport eq true&$select=id,name,isTosSupport,isOnlineTosSupport`
    ).then((response) => {
      if (response && Array.isArray(response.value)) {
        setBanks(response.value);
      }
    });
  };

  const getReceiverAccounts = () => {
    RequestHandler.get(
      `/FirmAccount?$filter=isTosVirmanAccount eq true&$select=id,iban,bankName,bankId,title&$top=1000`
    ).then((response) => {
      if (response && Array.isArray(response.value)) {
        setReceiverAccounts(response.value);
      }
    });
  };

  // Gönderen hesaplar ilk yüklemede değil, banka seçildikten sonra o bankaya göre yüklenir.
  // Uç (GetAllForOdata) zaten tenant'a ait aktif/silinmemiş kayıtları döndürür.
  const loadAccountsForBank = (bankId) => {
    if (!bankId || loadedBanks.includes(bankId)) return;
    RequestHandler.get(
      `/TenantPaymentAccount?$filter=bankId eq ${bankId}&$select=id,name,iban,accountNumber,bankId,tenantAccountId,bankInfoId&$top=1000`
    ).then((response) => {
      if (response && Array.isArray(response.value)) {
        setPaymentAccounts((prev) => {
          const ids = new Set(prev.map((x) => x.id));
          return [...prev, ...response.value.filter((x) => !ids.has(x.id))];
        });
        setLoadedBanks((prev) =>
          prev.includes(bankId) ? prev : [...prev, bankId]
        );
      }
    });
  };

  // Gönderen hesap, hesap hareketleri olan bir tenant hesabıyla eşleştirilmiş mi?
  const isAccountMatched = (senderAccountId) => {
    const acc = paymentAccounts.find((a) => a.id === senderAccountId);
    return !!acc && !!acc.tenantAccountId;
  };

  const fetchAccountBalance = (paymentAccountId) => {
    if (!paymentAccountId || !isAccountMatched(paymentAccountId)) {
      setAccountBalances((prev) => {
        const next = { ...prev };
        delete next[paymentAccountId];
        return next;
      });
      return;
    }
    RequestHandler.get(
      `/TenantPaymentAccount/GetBalanceByPaymentAccountId/${paymentAccountId}`
    ).then((response) => {
      if (response?.success && response.data !== null && response.data !== undefined) {
        setAccountBalances((prev) => ({
          ...prev,
          [paymentAccountId]: response.data,
        }));
      } else {
        setAccountBalances((prev) => ({ ...prev, [paymentAccountId]: null }));
      }
    });
  };

  const formatAccountLabel = (item) =>
    `${item.name ? item.name + " - " : ""}${item.iban || item.accountNumber || ""}`;

  const getCurrencies = () => {
    RequestHandler.get(`/Currency?$select=id,code`).then((response) => {
      if (response && Array.isArray(response.value)) {
        // Şimdilik yalnızca TRY seçilebilsin.
        setCurrencies(
          response.value.filter(
            (c) => c.id === TRY_CURRENCY_ID || c.code === "TRY"
          )
        );
      }
    });
  };

  const getTosBankInfos = () => {
    RequestHandler.get(
      `/TosBankInfo?$select=id,bankId,isUseEmail,isUseWebService,isUsePdf&$top=1000`
    ).then((response) => {
      if (response && Array.isArray(response.value)) {
        setTosBankInfos(response.value);
      }
    });
  };

  const hasOfflineTosBankSelected = (details) => {
    return (details || []).some((d) => {
      const bank = banks.find((b) => b.id === d.bankId);
      return bank && bank.isTosSupport && !bank.isOnlineTosSupport;
    });
  };

  // TÖS bağlantı türü SFTP: seçilen hesabın bağlı olduğu bankInfo'da
  // e-posta/web servis/pdf yöntemlerinin hiçbiri aktif değilse (varsayılan FTP/SFTP).
  const isSftpAccount = (senderAccountId) => {
    const acc = paymentAccounts.find((a) => a.id === senderAccountId);
    if (!acc || !acc.bankInfoId) return false;
    const info = tosBankInfos.find((t) => t.id === acc.bankInfoId);
    return (
      !!info && !info.isUseEmail && !info.isUseWebService && !info.isUsePdf
    );
  };

  const hasSftpAccountSelected = (details) =>
    (details || []).some((d) => isSftpAccount(d.senderAccountId));

  const buildPayload = (values) => ({
    name: values.name,
    receiverAccountId: values.receiverAccountId,
    executionTime: values.executionTime
      ? values.executionTime.format("HH:mm:ss")
      : null,
    frequencyInterval: values.frequencyInterval,
    frequencyPeriod: values.frequencyPeriod,
    endType: values.endType,
    endAfterOccurrences:
      values.endType === END_TYPE.AFTER_OCCURRENCE
        ? values.endAfterOccurrences
        : null,
    endDate:
      values.endType === END_TYPE.ON_DATE && values.endDate
        ? values.endDate.format("YYYY-MM-DDTHH:mm:ss")
        : null,
    details: (values.details || []).map((d) => ({
      id: d.id || null,
      bankId: d.bankId,
      senderAccountId: d.senderAccountId,
      description: d.description,
      amountToSend: d.sendAllBalance ? null : d.amountToSend,
      minRemainingBalance: d.sendAllBalance ? null : d.minRemainingBalance,
      sendAllBalance: !!d.sendAllBalance,
      currencyId: d.currencyId,
    })),
  });

  const submit = (values) => {
    const payload = buildPayload(values);
    setLoading(true);

    const request = editId
      ? RequestHandler.put(`/SweepRule`, { id: editId, ...payload })
      : RequestHandler.post(`/SweepRule`, payload);

    request.then((response) => {
      setLoading(false);
      if (response && response.success) {
        if (!editId) form.resetFields();
        if (onSuccess) onSuccess();
      }
    });
  };

  const onFinish = (values) => {
    // Offline TÖS bankası uyarısı: en geç saat 16:00 seçilebilir
    const tenAM = moment("16:00", "HH:mm");
    if (
      hasOfflineTosBankSelected(values.details) &&
      values.executionTime &&
      values.executionTime.isAfter(tenAM)
    ) {
      Modal.warning({
        title: "Uyarı",
        content:
          "Gönderen Hesaplar içerisinde offline yapıda bankanız bulunmaktadır. Para gönderimi için en geç saat 16:00 seçilebilir.",
        okText: "Saati Düzenle",
      });
      return;
    }

    submit(values);
  };

  // Yalnızca aktif TosBankInfo tanımı olan bankalar gönderen banka olarak seçilebilir.
  const tosDefinedBanks = banks.filter((b) =>
    tosBankInfos.some((t) => t.bankId === b.id)
  );

  return (
    <Form
      form={form}
      layout="vertical"
      name="sweepRuleForm"
      className="sweep-rule-form"
      onFinish={onFinish}
      onFinishFailed={() => setLoading(false)}
      initialValues={{
        frequencyInterval: 1,
        frequencyPeriod: 1,
        endType: END_TYPE.NEVER,
        details: [{ sendAllBalance: false, currencyId: TRY_CURRENCY_ID }],
      }}
    >
      <Divider orientation="left">Alıcı Hesap ve Zamanlama</Divider>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="name"
            label="Tanım Adı"
            rules={[{ required: true, whitespace: true, message: "Tanım adı gerekli." }]}
          >
            <Input placeholder="Tanım adı giriniz" maxLength={500} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="receiverAccountId"
            label="Alıcı Hesap"
            rules={[{ required: true, message: "Alıcı hesap seçiniz." }]}
          >
            <Select
              allowClear
              showSearch
              placeholder="Alıcı hesap seçiniz"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option.children || "")
                  .toLocaleLowerCase("tr-TR")
                  .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
              }
            >
              {receiverAccounts.map((item) => (
                <Option key={item.id} value={item.id}>
                  {`${item.bankName ? item.bankName + " - " : ""}${item.iban || ""}${
                    item.title ? " (" + item.title + ")" : ""
                  }`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={5}>
          <Form.Item
            name="executionTime"
            label="Gerçekleşme Saati"
            rules={[{ required: true, message: "Gerçekleşme saati seçiniz." }]}
          >
            <TimePicker
              style={{ width: "100%" }}
              format="HH:mm"
              placeholder="Saat seçiniz"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={7}>
          <Form.Item
            label={
              <span>
                Tekrar Etme Sıklığı{" "}
                <Tooltip title="Süpürme yalnızca hafta içi (Pazartesi-Cuma arasında) çalışır.">
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            required
          >
            <Space>
              <span>Her</span>
              <Form.Item
                name="frequencyInterval"
                noStyle
                rules={[{ required: true, message: "Değer giriniz." }]}
              >
                <InputNumber min={1} style={{ width: 80 }} />
              </Form.Item>
              <Form.Item
                name="frequencyPeriod"
                noStyle
                rules={[{ required: true, message: "Seçiniz." }]}
              >
                <Select style={{ width: 140 }}>
                  {frequencyPeriods.map((p) => (
                    <Option key={p.value} value={p.value}>
                      {p.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Space>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="endType" label="Ne Zaman Sonlansın?">
            <Radio.Group>
              <Radio value={END_TYPE.NEVER}>Sonlanmasın</Radio>
              <Radio value={END_TYPE.AFTER_OCCURRENCE}>Belirli tekrardan sonra</Radio>
              <Radio value={END_TYPE.ON_DATE}>Belirli bir tarihte</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.endType !== cur.endType}
          >
            {({ getFieldValue }) => {
              const endType = getFieldValue("endType");
              if (endType === END_TYPE.AFTER_OCCURRENCE) {
                return (
                  <Form.Item
                    name="endAfterOccurrences"
                    label="Tekrar Sayısı"
                    rules={[{ required: true, message: "Tekrar sayısı giriniz." }]}
                  >
                    <InputNumber
                      min={1}
                      style={{ width: "100%" }}
                      placeholder="Örn: 10"
                    />
                  </Form.Item>
                );
              }
              if (endType === END_TYPE.ON_DATE) {
                return (
                  <Form.Item
                    name="endDate"
                    label="Sonlanma Tarihi"
                    rules={[{ required: true, message: "Sonlanma tarihi seçiniz." }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD.MM.YYYY"
                      placeholder="Tarih seçiniz"
                    />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">Gönderen Hesaplar</Divider>

      <Form.List name="details">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Row
                gutter={8}
                key={key}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  padding: 8,
                  marginBottom: 8,
                }}
              >
                <Col xs={24} md={4}>
                  <Form.Item
                    {...restField}
                    name={[name, "bankId"]}
                    label="Banka"
                    rules={[{ required: true, message: "Banka seçiniz." }]}
                  >
                    <Select
                      showSearch
                      placeholder="Banka"
                      optionFilterProp="children"
                      onChange={(value) => {
                        // banka değişince gönderen hesabı temizle ve bağımlı select'i yenile
                        const details = form.getFieldValue("details");
                        if (details && details[name]) {
                          details[name].senderAccountId = undefined;
                          form.setFieldsValue({ details });
                        }
                        loadAccountsForBank(value);
                        setTick((t) => t + 1);
                      }}
                      filterOption={(input, option) =>
                        (option.children || "")
                          .toLocaleLowerCase("tr-TR")
                          .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                      }
                    >
                      {tosDefinedBanks.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={4}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => {
                      const p = prev.details && prev.details[name];
                      const c = cur.details && cur.details[name];
                      return (p && p.bankId) !== (c && c.bankId);
                    }}
                  >
                    {({ getFieldValue }) => {
                      const rowBankId = getFieldValue(["details", name, "bankId"]);
                      const activeBankInfoIds = new Set(
                        tosBankInfos.map((t) => t.id)
                      );
                      const options = paymentAccounts.filter(
                        (a) =>
                          a.bankId === rowBankId &&
                          activeBankInfoIds.has(a.bankInfoId)
                      );
                      return (
                        <Form.Item
                          {...restField}
                          name={[name, "senderAccountId"]}
                          label="Gönderen Hesap"
                          rules={[
                            { required: true, message: "Gönderen hesap seçiniz." },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Gönderen hesap"
                            optionFilterProp="children"
                            disabled={!rowBankId}
                            onChange={(val) => {
                              // Hesap eşleşmiyorsa min. bakiye ve tümünü gönder temizlensin
                              if (!isAccountMatched(val)) {
                                const details = form.getFieldValue("details");
                                if (details && details[name]) {
                                  details[name].sendAllBalance = false;
                                  details[name].minRemainingBalance = undefined;
                                  form.setFieldsValue({ details });
                                }
                              }
                              fetchAccountBalance(val);
                              setTick((t) => t + 1);
                            }}
                            filterOption={(input, option) =>
                              (option.children || "")
                                .toLocaleLowerCase("tr-TR")
                                .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                            }
                          >
                            {options.map((item) => (
                              <Option key={item.id} value={item.id}>
                                {formatAccountLabel(item)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>

                <Col xs={24} md={3}>
                  <Form.Item {...restField} name={[name, "description"]} label="Açıklama">
                    <Input placeholder="Açıklama" maxLength={1000} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={3}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => {
                      const p = prev.details && prev.details[name];
                      const c = cur.details && cur.details[name];
                      return (
                        (p && p.sendAllBalance) !== (c && c.sendAllBalance) ||
                        (p && p.senderAccountId) !== (c && c.senderAccountId) ||
                        (p && p.minRemainingBalance) !== (c && c.minRemainingBalance)
                      );
                    }}
                  >
                    {({ getFieldValue }) => {
                      const sendAll = getFieldValue([
                        "details",
                        name,
                        "sendAllBalance",
                      ]);
                      const senderId = getFieldValue([
                        "details",
                        name,
                        "senderAccountId",
                      ]);
                      const minBal = getFieldValue([
                        "details",
                        name,
                        "minRemainingBalance",
                      ]);
                      const matched = isAccountMatched(senderId);
                      const amountRequired =
                        !sendAll && !(matched && minBal != null);
                      return (
                        <Form.Item
                          {...restField}
                          name={[name, "amountToSend"]}
                          label="Gönderilecek Tutar"
                          rules={[
                            {
                              required: amountRequired,
                              message: "Tutar giriniz.",
                            },
                          ]}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: "100%" }}
                            placeholder="Tutar"
                            disabled={sendAll}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>

                <Col xs={24} md={3}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => {
                      const p = prev.details && prev.details[name];
                      const c = cur.details && cur.details[name];
                      return (
                        (p && p.senderAccountId) !== (c && c.senderAccountId) ||
                        (p && p.sendAllBalance) !== (c && c.sendAllBalance) ||
                        (p && p.minRemainingBalance) !== (c && c.minRemainingBalance)
                      );
                    }}
                  >
                    {({ getFieldValue }) => {
                      const senderId = getFieldValue([
                        "details",
                        name,
                        "senderAccountId",
                      ]);
                      const sendAll = getFieldValue([
                        "details",
                        name,
                        "sendAllBalance",
                      ]);
                      const matched = isAccountMatched(senderId);
                      const balance = accountBalances[senderId];
                      return (
                        <>
                          <Form.Item
                            {...restField}
                            name={[name, "minRemainingBalance"]}
                            label="Min. Kalan Bakiye"
                          >
                            <InputNumber
                              min={0}
                              style={{ width: "100%" }}
                              placeholder="Min. Bakiye"
                              disabled={!matched || sendAll}
                              onChange={(val) => {
                                if (val != null) {
                                  const details = form.getFieldValue("details");
                                  if (details && details[name]) {
                                    details[name].sendAllBalance = false;
                                    form.setFieldsValue({ details });
                                  }
                                }
                                setTick((t) => t + 1);
                              }}
                            />
                          </Form.Item>
                          {matched && balance != null && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#888",
                                marginTop: -16,
                                marginBottom: 8,
                              }}
                            >
                              Güncel Bakiye:{" "}
                              {Number(balance).toLocaleString("tr-TR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              TL
                            </div>
                          )}
                        </>
                      );
                    }}
                  </Form.Item>
                </Col>

                <Col xs={24} md={4}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => {
                      const p = prev.details && prev.details[name];
                      const c = cur.details && cur.details[name];
                      return (
                        (p && p.senderAccountId) !== (c && c.senderAccountId) ||
                        (p && p.sendAllBalance) !== (c && c.sendAllBalance) ||
                        (p && p.minRemainingBalance) !== (c && c.minRemainingBalance)
                      );
                    }}
                  >
                    {({ getFieldValue }) => {
                      const matched = isAccountMatched(
                        getFieldValue(["details", name, "senderAccountId"])
                      );
                      const minBal = getFieldValue([
                        "details",
                        name,
                        "minRemainingBalance",
                      ]);
                      return (
                        <Form.Item
                          {...restField}
                          name={[name, "sendAllBalance"]}
                          valuePropName="checked"
                          label="Tüm bakiyeyi gönder"
                          className="sweep-sendall-item"
                          style={{ textAlign: "center" }}
                        >
                          <Checkbox
                            disabled={!matched || minBal != null}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const details = form.getFieldValue("details");
                                if (details && details[name]) {
                                  details[name].minRemainingBalance = undefined;
                                  form.setFieldsValue({ details });
                                }
                              }
                              setTick((t) => t + 1);
                            }}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>

                <Col xs={12} md={2}>
                  <Form.Item
                    {...restField}
                    name={[name, "currencyId"]}
                    label="Para Birimi"
                    rules={[{ required: true, message: "Seçiniz." }]}
                  >
                    <Select placeholder="TRY" disabled>
                      {currencies.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.code}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={12} md={1}>
                  <Form.Item label=" ">
                    {fields.length > 1 && (
                      <MinusCircleOutlined
                        style={{ color: "red" }}
                        onClick={() => remove(name)}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() =>
                  add({ sendAllBalance: false, currencyId: TRY_CURRENCY_ID })
                }
                block
                icon={<PlusOutlined />}
              >
                Ekle
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) => prev.details !== cur.details}
      >
        {({ getFieldValue }) =>
          hasSftpAccountSelected(getFieldValue("details")) ? (
            <div
              style={{
                background: "#f6f6f6",
                padding: 12,
                borderRadius: 4,
                marginBottom: 16,
                fontSize: 12,
                color: "#666",
              }}
            >
              Bankada tanımlı TÖS yönteminize göre ödemeniz için ek onay
              gerekebilir. Gönderici hesapta talimatlı ödeme aktif ise, ödeme
              isteği bankaya iletilmiş olsa dahi son onay verilmeden ödeme
              gerçekleşmeyecektir. Bu süreçte, bankanızda mevcutta tanımlı TÖS
              akışınız esas alınacak olup, burada da aynı onay akışı
              uygulanacaktır.
            </div>
          ) : null
        }
      </Form.Item>

      <div style={{ textAlign: "right" }}>
        <Button type="primary" htmlType="submit" loading={loading}>
          Kaydet
        </Button>
      </div>
    </Form>
  );
};

export default SweepRuleAddForm;
